import React from 'react';
import clsx from 'clsx';
import { Checkbox } from '@components/common';
import Tooltip from '@components/common/Tooltip';
import styles from './FilterButtons.module.css';

export interface FilterOption {
  key: string;
  label: string;
  type?: 'danger' | 'warning' | 'primary';
  tooltip?: string;
  /** For states the API does not count (e.g. Pending Reschedule): a 0 would mislead. */
  hideCount?: boolean;
}

interface FilterButtonsProps {
  filters: readonly FilterOption[];
  activeFilter: string;
  onFilterChange: (filter: string) => void;
  filterCounts: { [key: string]: number };
  showCheckbox?: boolean;
  checkboxLabel?: string;
  checkboxChecked?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  dataTestId?: string;
}

export const FilterButtons: React.FC<FilterButtonsProps> = ({
  filters,
  activeFilter,
  onFilterChange,
  filterCounts,
  showCheckbox = false,
  checkboxLabel = '',
  checkboxChecked = false,
  onCheckboxChange,
  dataTestId = 'filter-buttons'
}) => {
  // Separate filters by type: danger first, then warning, then primary
  const dangerFilters = filters.filter((f) => f.type === 'danger');
  const warningFilters = filters.filter((f) => f.type === 'warning');
  const primaryFilters = filters.filter((f) => !f.type || (f.type !== 'danger' && f.type !== 'warning'));
  const hasDangerFilters = dangerFilters.length > 0;
  const hasWarningFilters = warningFilters.length > 0;
  const hasPrimaryFilters = primaryFilters.length > 0;

  return (
    <>
      {/* Filter Buttons */}
      <div className={styles.filterButtons}>
        {/* Danger filters */}
        {dangerFilters.map((filter) => {
          const button = (
            <button
              key={filter.key}
              className={clsx(
                styles.filterButtonsButton,
                styles.filterButtonsButtonDanger,
                activeFilter === filter.key && styles.filterButtonsButtonActive
              )}
              onClick={() => onFilterChange(filter.key)}
              data-test-id={`${dataTestId}-button-${filter.key}`}
            >
              {filter.label}
              {!filter.hideCount && <span className={styles.filterButtonsCount}>{filterCounts[filter.key] || 0}</span>}
            </button>
          );

          return filter.tooltip ? (
            <Tooltip key={filter.key} content={filter.tooltip} position="bottom" width={240}>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}

        {/* Divider between danger and warning */}
        {hasDangerFilters && (hasWarningFilters || hasPrimaryFilters) && (
          <div className={styles.filterButtonsDivider} />
        )}

        {/* Warning filters */}
        {warningFilters.map((filter) => (
          <button
            key={filter.key}
            className={clsx(
              styles.filterButtonsButton,
              styles.filterButtonsButtonWarning,
              activeFilter === filter.key && styles.filterButtonsButtonActive
            )}
            onClick={() => onFilterChange(filter.key)}
            data-test-id={`${dataTestId}-button-${filter.key}`}
          >
            {filter.label}
            {!filter.hideCount && <span className={styles.filterButtonsCount}>{filterCounts[filter.key] || 0}</span>}
          </button>
        ))}

        {/* Divider between warning and primary */}
        {hasWarningFilters && hasPrimaryFilters && <div className={styles.filterButtonsDivider} />}

        {/* Primary filters */}
        {primaryFilters.map((filter) => (
          <button
            key={filter.key}
            className={clsx(styles.filterButtonsButton, activeFilter === filter.key && styles.filterButtonsButtonActive)}
            onClick={() => onFilterChange(filter.key)}
            data-test-id={`${dataTestId}-button-${filter.key}`}
          >
            {filter.label}
            {!filter.hideCount && <span className={styles.filterButtonsCount}>{filterCounts[filter.key] || 0}</span>}
          </button>
        ))}
      </div>

      {/* Optional Checkbox Filter */}
      {showCheckbox && (
        <div className={styles.filterButtonsCheckboxFilter} data-test-id={`${dataTestId}-checkbox-filter`}>
          <Checkbox
            label={checkboxLabel}
              checked={checkboxChecked}
              onChange={(e) => onCheckboxChange?.(e.target.checked)}
            wrapperClassName={styles.filterButtonsCheckboxWrapper}
            dataTestId={`${dataTestId}-checkbox`}
            />
        </div>
      )}
    </>
  );
};
